import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import MapDisplay from '../components/Map/MapDisplay';
import DrawingTools, { TOOLS } from '../components/Map/DrawingTools';
import TokenManager from '../components/Map/TokenManager';
import ColorPicker from '../components/Map/ColorPicker';
import LayerManager from '../components/Map/LayerManager';
import UndoRedoButtons from '../components/Map/UndoRedoButtons';
import HistoryDebugPanel from '../components/Map/HistoryDebugPanel';
import MemoryWarning from '../components/Map/MemoryWarning';
import SaveGameModal from '../components/SaveLoad/SaveGameModal';
import LoadGameModal from '../components/SaveLoad/LoadGameModal';
import { useLayers } from '../context/LayerContext';
import { useHistory, COMMAND_TYPES } from '../context/HistoryContext';
import { useContainers } from '../context/ContainerContext';
import { useGames } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import GameStateManager from '../services/GameStateManager';
import workstateManager from '../services/WorkstateManager';
import ReAuthModal from '../components/Auth/ReAuthModal';
import { safeNavigate } from '../utils/navigationUtils';
import './Pages.css';
import './MapEditorPage.css';
import LineBasedFOVAdapter from '../components/Map/models/LineBasedFOVAdapter';

// Inner component that uses the layer context (must be inside LayerProvider)
const MapEditorInner = ({ mapId, gameName, setGameName, isSharedGame, setIsSharedGame, tokens, setTokens, selectedTokenIds, setSelectedTokenIds, tokenGroups, setTokenGroups, nextGroupId, setNextGroupId, groupLightingEnabled, setGroupLightingEnabled }) => {
  const { layers, activeLayerId } = useLayers();
  const { containers } = useContainers();
  const { currentUser } = useAuth() || {};
  const historyContext = useHistory();
  const authContext = useAuth();
  const navigate = useNavigate();
  
  // Create GameStateManager instance
  const gameStateManager = useMemo(() => new GameStateManager(), []);
  
  // Get full context objects for save/load operations
  const layerContext = useLayers();
  const containerContext = useContainers();
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [isEditMode, setIsEditMode] = useState(true);
  const [areDrawingsVisible, setAreDrawingsVisible] = useState(true);
  const [isDrawingUnlocked, setIsDrawingUnlocked] = useState(false);
  const [toolOptions, setToolOptions] = useState({
    brushSize: 5,
    eraserSize: 20,
    wallThickness: 3,
    color: '#000000',
    tokenSize: 30,
    shapeBlockMode: 'standardWall'
  });

  // Save/Load modal states
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [saveLoadStatus, setSaveLoadStatus] = useState('');
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [showGroupColorPicker, setShowGroupColorPicker] = useState(false);
  const [groupColorAnchor, setGroupColorAnchor] = useState(null);
  const [selectedGroupForColor, setSelectedGroupForColor] = useState(null);
  
  // New game confirmation dialog state
  const [showNewGameConfirm, setShowNewGameConfirm] = useState(false);
  
  // Add notification state for simple notifications
  const [notification, setNotification] = useState('');
  
  // ReAuth modal state
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  
  // Auto-save state
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [showAutoSaveMenu, setShowAutoSaveMenu] = useState(false);
  const [autoSaveInterval, setAutoSaveInterval] = useState(30); // in seconds
  const [autoSaveTimeLeft, setAutoSaveTimeLeft] = useState(30);
  const [hasUnsavedWork, setHasUnsavedWork] = useState(false);
  const [fovAdapter, setFovAdapter] = useState(null);
  // Admin debug panel visibility (global setting via localStorage)
  const [showHistoryDebugPanel, setShowHistoryDebugPanel] = useState(() => {
    try {
      return localStorage.getItem('dnd_show_history_debug_panel') === 'true';
    } catch (_) {
      return false;
    }
  });
  
  // Unsaved changes warning modal state
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [navigationCallback, setNavigationCallback] = useState(null);
  
  const mapDisplayRef = useRef(null);
  
  const { saveGame, setUnsavedChangesState, setNavigationProtection } = useGames();
  
  // Add history context - now this is inside HistoryProvider
  const { pushCommand } = useHistory();
  
  // Create FOV adapter directly instead of trying to get it from MapDisplay
  useEffect(() => {
    if (!fovAdapter) {
      const adapter = new LineBasedFOVAdapter({ enabled: true });
      setFovAdapter(adapter);
    }
  }, [fovAdapter]);

  // Listen to storage changes (e.g., toggled from Profile page in another tab)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'dnd_show_history_debug_panel') {
        setShowHistoryDebugPanel(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  
  // Update FOV adapter with walls when layers change
  useEffect(() => {
    if (fovAdapter && layers && layers.length > 0) {
      try {
        fovAdapter.updateWallsFromLayers(layers);
      } catch (error) {
        console.error('[MapEditorPage] Error updating FOV adapter with walls:', error);
      }
    }
  }, [fovAdapter, layers]); // Update when fovAdapter or layers change
  
  const toggleDrawingsVisibility = () => {
    setAreDrawingsVisible(prev => !prev);
  };
  
  const handleToolChange = (tool) => {
    setActiveTool(tool);
    // Remove automatic color changes - keep the current color across all tools
    // This allows color synchronization between tools
  };
  
  const handleOptionChange = (option, value) => {
    setToolOptions(prev => ({ ...prev, [option]: value }));
  };
  
  const handleTokenSelect = (tokenIds, ctrlKey = false, isDragStart = false, isDoubleClick = false) => {
    if (Array.isArray(tokenIds)) {
      setSelectedTokenIds(tokenIds);
    } else {
      // Check if this token is part of a group and handle double-click
      if (isDoubleClick) {
        const tokenGroup = tokenGroups.find(group => group.tokenIds.includes(tokenIds));
        if (tokenGroup) {
          // Select all tokens in the group
          setSelectedTokenIds(tokenGroup.tokenIds);
          return;
        }
      }
      
      // Single token selection
      if (ctrlKey) {
        // Ctrl+click - toggle token in selection
        setSelectedTokenIds(prevIds => 
          prevIds.includes(tokenIds) 
            ? prevIds.filter(id => id !== tokenIds)
            : [...prevIds, tokenIds]
        );
      } else {
        // Normal click - select only this token
        // But if it's a drag start and this token is already part of multi-selection, don't change selection
        if (isDragStart) {
          setSelectedTokenIds(prevIds => {
            if (prevIds.includes(tokenIds) && prevIds.length > 1) {
              // Token is already selected as part of multi-selection, keep the selection as is
              return prevIds;
            } else {
              // Token is not selected or is the only selected token, select just this one
              return [tokenIds];
            }
          });
        } else {
          // Regular click, always select just this token
          setSelectedTokenIds([tokenIds]);
        }
      }
    }
  };
  
  // Available colors for token groups (same as layer colors)
  const groupColors = useMemo(() => [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', 
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6c757d'
  ], []);

  // Function to show notification (similar to saveLoadStatus)
  const showNotification = useCallback((message, duration = 3000) => {
    setNotification(message);
    setTimeout(() => setNotification(''), duration);
  }, []);

  // Function to collect current game state for auto-save
  const collectCurrentGameState = useCallback(async () => {
    try {
      // Skip auto-save if we're on a new game that hasn't been saved yet
      // and there's no meaningful content
      const currentPath = window.location.pathname;
      const isNewGame = currentPath.includes('/new') || !mapId || mapId === 'new';
      
      if (isNewGame) {
        // Only save if there's actual content
        const hasContent = (
          (tokens && tokens.length > 0) ||
          (layers && layers.some(layer => layer.drawings && layer.drawings.length > 0)) ||
          (containers && containers.length > 0)
        );
        
                 if (!hasContent) {
           return null;
         }
      }

      const contexts = {
        layerContext,
        containerContext,
        gameContext: {},
        historyContext,
        authContext,
        tokenContext: {
          tokens: tokens || [],
          setTokens: () => {},
          selectedTokenIds: selectedTokenIds || [],
          setSelectedTokenIds: () => {},
          tokenGroups: tokenGroups || [],
          setTokenGroups: () => {}
        }
      };

      const gameState = await gameStateManager.collectGameState(contexts);
      return gameState;
    } catch (error) {
      return null;
    }
  }, [layerContext, containerContext, historyContext, authContext, gameStateManager, tokens, selectedTokenIds, tokenGroups, mapId, layers, containers]);

  // Auto-save timer for countdown
  useEffect(() => {
    if (!autoSaveEnabled) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastSave = lastAutoSave ? now - new Date(lastAutoSave).getTime() : 0;
      const intervalMs = autoSaveInterval * 1000;
      const nextSaveIn = Math.max(0, intervalMs - (timeSinceLastSave % intervalMs));
      const timeLeft = Math.ceil(nextSaveIn / 1000);
      setAutoSaveTimeLeft(timeLeft);
    }, 1000);

    return () => clearInterval(interval);
  }, [autoSaveEnabled, lastAutoSave, autoSaveInterval]);

  // Update countdown when interval changes
  useEffect(() => {
    if (!lastAutoSave && autoSaveEnabled) {
      setAutoSaveTimeLeft(autoSaveInterval);
    }
  }, [autoSaveInterval, lastAutoSave, autoSaveEnabled]);

  // Close auto-save menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAutoSaveMenu && !event.target.closest('.quick-save-container')) {
        setShowAutoSaveMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAutoSaveMenu]);

  // Function to restore work state
  const restoreWorkState = useCallback(async (gameState) => {
    try {
      const contexts = {
        layerContext,
        containerContext,
        gameContext: {},
        historyContext,
        authContext,
        tokenContext: {
          tokens: [],
          setTokens: setTokens,
          selectedTokenIds: [],
          setSelectedTokenIds: setSelectedTokenIds,
          tokenGroups: [],
          setTokenGroups: setTokenGroups
        }
      };

      await gameStateManager.restoreGameState(gameState, contexts);
      
      // Update game name if available
      if (gameState.metadata?.name) {
        setGameName(gameState.metadata.name);
      }

      showNotification('✅ Your work has been restored!', 5000);
      workstateManager.clearWorkState();
      setHasUnsavedWork(false); // Clear the unsaved work state
    } catch (error) {
      showNotification('⚠️ Failed to restore work state', 5000);
    }
  }, [layerContext, containerContext, historyContext, authContext, gameStateManager, setTokens, setSelectedTokenIds, setTokenGroups, setGameName, showNotification]);

  // Initialize auto-save and auth event listeners
  useEffect(() => {
    // Start auto-save
    if (autoSaveEnabled) {
      workstateManager.startAutoSave(async () => {
        const gameState = await collectCurrentGameState();
        if (gameState) {
          setLastAutoSave(new Date().toISOString());
          return gameState;
        }
        // Update last auto save time even if no content to save
        // This keeps the timer running properly
        setLastAutoSave(new Date().toISOString());
        return null;
      }, autoSaveInterval);
    } else {
      workstateManager.stopAutoSave();
    }

    // Listen for auth required events
    const handleAuthRequired = (event) => {
      // Save current work before showing auth modal
      collectCurrentGameState().then(gameState => {
        if (gameState) {
          workstateManager.saveWorkState(gameState, {
            userId: authContext?.currentUser?.userId,
            gameName: gameName || 'Untitled Game'
          });
        }
      });
      
      setShowReAuthModal(true);
    };

    // Listen for auth success events
    const handleAuthSuccess = (event) => {
      showNotification(event.detail.message, 5000);
      
      // Check if there's work to restore
      const workInfo = workstateManager.getWorkStateInfo();
      if (workInfo) {
        // Automatically restore work
        setTimeout(() => {
          const workState = workstateManager.getWorkState();
          if (workState?.gameState) {
            restoreWorkState(workState.gameState);
          }
        }, 1000);
      }
    };

    window.addEventListener('authRequired', handleAuthRequired);
    window.addEventListener('authSuccess', handleAuthSuccess);

    return () => {
      workstateManager.stopAutoSave();
      window.removeEventListener('authRequired', handleAuthRequired);
      window.removeEventListener('authSuccess', handleAuthSuccess);
    };
  }, [collectCurrentGameState, authContext, gameName, showNotification, autoSaveEnabled, autoSaveInterval, restoreWorkState]);

  // Function to handle restore from auto-save button
  const handleRestoreAutoSave = useCallback(() => {
    const workState = workstateManager.getWorkState();
    if (workState?.gameState) {
      restoreWorkState(workState.gameState);
    }
  }, [restoreWorkState]);

  // Check for unsaved work on component mount
  useEffect(() => {
    const workInfo = workstateManager.getWorkStateInfo();
    setHasUnsavedWork(!!workInfo);
  }, []); // Only run once on mount

  // Check if user has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // Check if there's content that hasn't been saved
    const hasContent = (
      (tokens && tokens.length > 0) ||
      (layers && layers.some(layer => layer.drawings && layer.drawings.length > 0)) ||
      (containers && containers.length > 0) ||
      (gameName && gameName.trim() !== '' && gameName !== 'Untitled Game')
    );
    
    // If it's a new game without a mapId and has content, consider it unsaved
    const isNewGameWithContent = (!mapId || mapId === 'new') && hasContent;
    
    // Or if we explicitly have unsaved work tracked
    return isNewGameWithContent || hasUnsavedWork;
  }, [tokens, layers, containers, gameName, mapId, hasUnsavedWork]);

  // Prevent navigation if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // For other browsers
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnsavedChanges()) {
        // Optionally save state when page becomes hidden
        collectCurrentGameState().then(gameState => {
          if (gameState) {
            workstateManager.saveWorkState(gameState, {
              userId: authContext?.currentUser?.userId,
              gameName: gameName || 'Untitled Game'
            });
          }
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasUnsavedChanges, collectCurrentGameState, authContext, gameName]);

  // Handle navigation confirmation
  const handleNavigationAttempt = useCallback((callback) => {
    if (hasUnsavedChanges()) {
      setNavigationCallback(() => callback);
      setShowUnsavedChangesModal(true);
    } else {
      callback();
    }
  }, [hasUnsavedChanges]);

  const handleConfirmNavigation = useCallback(() => {
    setShowUnsavedChangesModal(false);
    if (navigationCallback) {
      navigationCallback();
      setNavigationCallback(null);
    }
  }, [navigationCallback]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setNavigationCallback(null);
  }, []);

  // Set up navigation protection in GameContext
  useEffect(() => {
    setNavigationProtection(handleNavigationAttempt);
  }, [handleNavigationAttempt, setNavigationProtection]);

  // Update GameContext with unsaved changes state
  useEffect(() => {
    const changesState = hasUnsavedChanges();
    setUnsavedChangesState(changesState);
  }, [hasUnsavedChanges, setUnsavedChangesState]);

  // Function to create a group from selected tokens
  const handleCreateTokenGroup = useCallback(() => {
    if (selectedTokenIds.length < 2) {
      showNotification('Please select at least 2 tokens to create a group');
      return;
    }
    
    // Assign color cyclically based on existing groups
    const colorIndex = tokenGroups.length % groupColors.length;
    const assignedColor = groupColors[colorIndex];
    
    const newGroup = {
      id: nextGroupId,
      name: `Group ${nextGroupId}`,
      tokenIds: [...selectedTokenIds],
      color: assignedColor
    };
    
    setTokenGroups(prev => [...prev, newGroup]);
    setNextGroupId(prev => prev + 1);
    
    // Add history command for group creation
    pushCommand({
      type: COMMAND_TYPES.CREATE_TOKEN_GROUP,
      groupId: newGroup.id,
      groupData: newGroup,
      selectedTokenIds: selectedTokenIds
    });
  }, [selectedTokenIds, nextGroupId, setTokenGroups, setNextGroupId, pushCommand, tokenGroups.length, groupColors, showNotification]);
  
  // Function to ungroup tokens
  const handleUngroupTokens = useCallback(() => {
    const groupsToRemove = tokenGroups.filter(group => 
      group.tokenIds.some(tokenId => selectedTokenIds.includes(tokenId))
    );
    
    if (groupsToRemove.length === 0) {
      showNotification('Selected tokens are not part of any group');
      return;
    }
    
    // Remove the groups
    setTokenGroups(prev => prev.filter(group => 
      !groupsToRemove.some(removeGroup => removeGroup.id === group.id)
    ));
    
    // Add history command for ungrouping
    groupsToRemove.forEach(group => {
      pushCommand({
        type: COMMAND_TYPES.REMOVE_TOKEN_GROUP,
        groupId: group.id,
        groupData: group,
        selectedTokenIds: selectedTokenIds
      });
    });
  }, [selectedTokenIds, tokenGroups, setTokenGroups, pushCommand, showNotification]);

  // Combined function for grouping/ungrouping tokens (like layers)
  const handleToggleTokenGroup = useCallback(() => {
    if (selectedTokenIds.length === 0) {
      return;
    }
    
    // Check if any selected tokens are already in groups
    const hasGroupedTokens = selectedTokenIds.some(tokenId => 
      tokenGroups.some(group => group.tokenIds.includes(tokenId))
    );
    
    if (hasGroupedTokens) {
      // Ungroup - remove selected tokens from their groups
      handleUngroupTokens();
    } else {
      // Group - create new group if we have 2+ tokens
      if (selectedTokenIds.length >= 2) {
        handleCreateTokenGroup();
      } else {
        showNotification('Please select at least 2 tokens to create a group');
      }
    }
    }, [selectedTokenIds, tokenGroups, handleUngroupTokens, handleCreateTokenGroup, showNotification]);

  // Direct functions for undo/redo (don't create new commands)
  const createTokenGroupDirect = useCallback((groupData) => {
    setTokenGroups(prev => [...prev, groupData]);
    setNextGroupId(prev => Math.max(prev, groupData.id + 1));
  }, [setTokenGroups, setNextGroupId]);

  const removeTokenGroupDirect = useCallback((groupId) => {
    setTokenGroups(prev => prev.filter(group => group.id !== groupId));
  }, [setTokenGroups]);

  // Direct function to update token group properties (for undo/redo)
  const updateTokenGroupDirect = useCallback((groupId, updates) => {
    setTokenGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, [setTokenGroups]);

  // Function to change group color
  const handleChangeGroupColor = useCallback((anchorElement) => {
    // Find the group that contains the selected tokens
    const selectedGroup = tokenGroups.find(group => 
      group.tokenIds.some(tokenId => selectedTokenIds.includes(tokenId))
    );
    
    if (!selectedGroup) {
      return;
    }
    
    // Ensure anchor element is valid and connected to DOM
    if (!anchorElement || !anchorElement.isConnected) {
      console.warn('ColorPicker: Invalid anchor element provided');
      return;
    }
    
    setSelectedGroupForColor(selectedGroup);
    setGroupColorAnchor(anchorElement);
    setShowGroupColorPicker(true);
  }, [tokenGroups, selectedTokenIds]);

  // Handle toggle of group lighting mode
  const handleToggleGroupLighting = useCallback(() => {
    if (selectedTokenIds.length < 2) {
      showNotification('Please select at least 2 tokens to enable group lighting');
      return;
    }
    setGroupLightingEnabled(prev => !prev);
  }, [selectedTokenIds.length, showNotification, setGroupLightingEnabled]);

  const handleGroupColorChange = useCallback((newColor) => {
    if (!selectedGroupForColor) return;
    
    // Update group color
    setTokenGroups(prev => prev.map(group => 
      group.id === selectedGroupForColor.id 
        ? { ...group, color: newColor }
        : group
    ));
    
    // Add to history
    pushCommand({
      type: 'UPDATE_TOKEN_GROUP_COLOR',
      groupId: selectedGroupForColor.id,
      previousColor: selectedGroupForColor.color,
      newColor: newColor,
      selectedTokenIds: selectedTokenIds
    });
  }, [selectedGroupForColor, setTokenGroups, pushCommand, selectedTokenIds]);

  const handleGroupColorClose = useCallback(() => {
    // Add a small delay to prevent race conditions
    setTimeout(() => {
      setShowGroupColorPicker(false);
      setGroupColorAnchor(null);
      setSelectedGroupForColor(null);
    }, 10);
  }, []);
  
  const handleUpdateToken = (tokenId, updates) => {
    setTokens(prev => prev.map(t => t.id === tokenId ? {...t, ...updates} : t));
  };
  
  const handleDeleteToken = (tokenIdToDelete) => {
    // Use functional updates so multiple rapid deletions compose correctly
    setTokens(prevTokens => prevTokens.filter(t => t.id !== tokenIdToDelete));
    setSelectedTokenIds(prevIds => prevIds.filter(id => id !== tokenIdToDelete));
    setTokenGroups(prevGroups => {
      const updatedGroups = prevGroups
        .map(group => ({
          ...group,
          tokenIds: group.tokenIds.filter(id => id !== tokenIdToDelete)
        }))
        .filter(group => group.tokenIds.length > 0);
      return updatedGroups;
    });
  };
  
  const handleNewTokenCreated = useCallback((newTokenData) => {
    // Push command to history for token creation
    pushCommand({
      type: COMMAND_TYPES.ADD_TOKEN,
      targetId: newTokenData.id,
      newState: { ...newTokenData },
      selectedTokenIds: selectedTokenIds, // Previous selection
      nextSelectedTokenIds: [newTokenData.id] // New selection after creation
    });
    
    setTokens(prevTokens => [...prevTokens, newTokenData]);
    setSelectedTokenIds([newTokenData.id]);
    // Optionally, switch back to select tool after adding a token
    // setActiveTool(TOOLS.SELECT); 
  }, [pushCommand, selectedTokenIds, setTokens, setSelectedTokenIds]);
  
  const handleModeToggle = () => {
    // If going from edit mode to play mode, activate fog immediately
    if (isEditMode) {
      // Call the method to immediately show black cover before mode changes
      mapDisplayRef.current?.activateFogImmediately();
      // Wait for next frame to ensure black cover is rendered before mode changes
      requestAnimationFrame(() => {
        setIsEditMode(false);
        // Set the select tool when switching to play mode
        setActiveTool(TOOLS.SELECT);
      });
    } else {
      // Just switch back to edit mode immediately
      setIsEditMode(true);
    }
  };


  const handleSaveAsCopy = async () => {
    if (!isSharedGame) return;
    const newName = prompt('Enter a name to save this shared game as your own:');
    if (!newName || newName.trim() === '') { 
      showNotification('Please enter a valid game name'); 
      return; 
    }
    const trimmedName = newName.trim();
    const currentTokens = mapDisplayRef.current?.getTokens ? mapDisplayRef.current.getTokens() : tokens;
    const gameState = { game_name: trimmedName, map_configuration_json: JSON.stringify({ tokens: currentTokens }) };
    try {
      const savedGame = await saveGame(gameState);
      if (savedGame) {
        showNotification(`Game saved as "${trimmedName}" in your personal games!`);
        setIsSharedGame(false); setGameName(trimmedName);
      }
    } catch (err) { 
      console.error('Error saving copy of game:', err); 
      showNotification('Failed to save a copy of this game.', 5000); 
    }
  };

  // New Save/Load functions
  const handleOpenSaveModal = () => {
    setIsSaveModalOpen(true);
  };

  // הוספת פונקציה לשמירה מהירה
  const handleQuickSave = async () => {
    // ודא שיש לאן לשמור (משחק קיים)
    if (!mapId || mapId === 'new' || isNaN(parseInt(mapId))) {
      // אם אין לאן לשמור, פתח מודל שמירה
      handleOpenSaveModal();
      return;
    }

    setIsQuickSaving(true); // התחלת אנימציית הטעינה

    try {
      const contexts = {
        layerContext,
        containerContext,
        gameContext: {},
        historyContext: historyContext,
        authContext: authContext,
        tokenContext: {
          tokens: tokens || [],
          setTokens: () => {},
                  selectedTokenIds: selectedTokenIds || [],
        setSelectedTokenIds: () => {}
        }
      };

      const gameStateManager = new GameStateManager();
      const gameState = await gameStateManager.collectGameState(contexts);
      
      const saveOptions = {
        name: gameName,
        description: '',
        isPublic: false,
        userId: authContext.currentUser?.userId,
        gameStateId: mapId
      };

      const saveResult = await gameStateManager.updateGameState(mapId, gameState, saveOptions);
      
      if (!saveResult.success) {
        throw new Error(saveResult.error);
      }

      setSaveLoadStatus('Game saved successfully!');
      setTimeout(() => setSaveLoadStatus(''), 3000);

    } catch (error) {
      console.error('Error in quick save:', error);
      setSaveLoadStatus(`Error saving: ${error.message}`);
      setTimeout(() => setSaveLoadStatus(''), 5000);
    } finally {
      setIsQuickSaving(false); // סיום אנימציית הטעינה
    }
  };

  const handleOpenLoadModal = () => {
    setIsLoadModalOpen(true);
  };

  const handleCreateNewGame = () => {
    // Show custom confirmation dialog
    setShowNewGameConfirm(true);
  };

  const handleConfirmNewGame = () => {
    // Close confirmation dialog
    setShowNewGameConfirm(false);

    // Clear all current game state locally
    layerContext.clearAllLayers();
    containerContext.clearAllContainers();
    historyContext.clearHistory();
    setTokens([]);
    setSelectedTokenIds([]);
    setTokenGroups([]);
    setNextGroupId(1);
    setGameName('');
    setIsSharedGame(false);

    // Clear any saved work state
    workstateManager.clearWorkState();

    // Clear the last game ID from localStorage so Header will show /app instead of old gameId
    localStorage.removeItem('dnd_last_game_id');

    // Create initial layers first, before navigation
    layerContext.createInitialGameLayers();
    
    // Navigate directly to /app for new game
      safeNavigate(navigate, '/app', { replace: true });

    // Show success message after a short delay to ensure navigation completes
    setTimeout(() => {
    showNotification('New game created successfully!', 3000);
    }, 200);
  };

  const handleCancelNewGame = () => {
    setShowNewGameConfirm(false);
  };

  const handleSaveSuccess = (result) => {
    setSaveLoadStatus(`Game "${result.name}" saved successfully!`);
    setTimeout(() => setSaveLoadStatus(''), 5000);
    
    // עדכון localStorage עם ה-gameId החדש
    if (result.gameId) {
      localStorage.setItem('dnd_last_game_id', result.gameId);
      
      // אם זה משחק חדש (mapId הוא 'new'), נעדכן את ה-URL
      if (mapId === 'new') {
        safeNavigate(navigate, `/app?gameId=${result.gameId}`, { replace: true });
      }
    }
  };

  const handleLoadSuccess = (result) => {
    setSaveLoadStatus(`Game "${result.name}" loaded successfully!`);
    setTimeout(() => setSaveLoadStatus(''), 5000);
    setGameName(result.name);
    
    // עדכון localStorage עם ה-gameId החדש
    if (result.gameId) {
      localStorage.setItem('dnd_last_game_id', result.gameId);
    }
  };

  const handleExportLocal = async () => {
    try {
      // Collect current game state using the contexts already available in the component
      const contexts = {
        layerContext,
        containerContext,
        gameContext: {},
        historyContext: historyContext,
        authContext: authContext
      };

      const gameState = await gameStateManager.collectGameState(contexts);
      
      // Export locally
      const filename = `${gameName || 'game-state'}_${new Date().toISOString().split('T')[0]}.json`;
      const result = gameStateManager.exportGameStateLocally(gameState, filename);
      
      if (result.success) {
        setSaveLoadStatus('Game exported successfully!');
        setTimeout(() => setSaveLoadStatus(''), 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error exporting game:', error);
      showNotification(`Error exporting game: ${error.message}`, 5000);
    }
  };

    const handleImportLocal = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await gameStateManager.importGameStateLocally(file);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      // Confirm import
      const confirmed = window.confirm(
        `Are you sure you want to load the game from file?\n\nThis action will delete all current content on the map.`
      );

      if (!confirmed) {
        event.target.value = ''; // Clear file input
        return;
      }

      // Restore game state using the contexts already available in the component
      const contexts = {
        layerContext,
        containerContext,
        gameContext: {},
        historyContext,
        tokenContext: {
          tokens: [], // Start with empty array for loading
          setTokens: setTokens,
          selectedTokenIds: [], // Start with no selection for loading
          setSelectedTokenIds: setSelectedTokenIds
        }
      };

      await gameStateManager.restoreGameState(result.gameState, contexts);
      
      setSaveLoadStatus('Game imported successfully!');
      setTimeout(() => setSaveLoadStatus(''), 3000);
      
      // Update game name if available
      if (result.gameState.metadata?.name) {
        setGameName(result.gameState.metadata.name);
      }

    } catch (error) {
      console.error('Error importing game:', error);
      showNotification(`Error importing game: ${error.message}`, 5000);
    } finally {
      event.target.value = ''; // Clear file input
    }
  };

  // Function to restore a deleted token (for undo operations)
  const handleRestoreToken = useCallback((tokenData) => {
    setTokens(prevTokens => {
      // Check if token already exists (avoid duplicates)
      if (prevTokens.find(t => t.id === tokenData.id)) {
        return prevTokens;
      }
      return [...prevTokens, tokenData];
    });
  }, [setTokens]);

  // Check if current active layer is non-wall layer and tools should be locked
  const isNonWallLayerActive = useMemo(() => {
    if (!activeLayerId || !layers || isDrawingUnlocked) return false;
    const activeLayer = layers.find(layer => layer.layer_id === activeLayerId);
    return activeLayer && !activeLayer.is_wall_layer;
  }, [activeLayerId, layers, isDrawingUnlocked]);

  // Handle unlock request for drawing on non-wall layer
  const handleUnlockRequest = useCallback((tool) => {
    // Create custom dialog with two buttons
    const dialogContainer = document.createElement('div');
    dialogContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: var(--font-primary);
      animation: fadeIn 0.3s ease-out;
    `;
    
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      color: var(--text-color);
      padding: 0;
      border-radius: 12px;
      border: 3px solid var(--accent-color);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6), 
                  0 0 30px rgba(160, 44, 44, 0.4);
      max-width: 500px;
      width: 90%;
      font-family: var(--font-primary);
      animation: slideInScale 0.3s ease-out;
    `;
    
    dialog.innerHTML = `
      <div style="
        background: rgba(0, 0, 0, 0.2);
        padding: var(--spacing-lg);
        border-radius: 9px 9px 0 0;
        border-bottom: 2px solid var(--accent-color);
      ">
        <h3 style="
          margin: 0;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 1.4rem;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
          color: var(--accent-color);
        ">Drawing on Image Layer</h3>
      </div>
      <div style="
        padding: var(--spacing-lg);
        line-height: 1.6;
      ">
        <p style="
          margin: 0 0 var(--spacing-md) 0;
          font-size: 1.1rem;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
          color: var(--text-color);
        ">
          Note that drawing on a layer that is not a wall layer (🧱) will not block light during gameplay. 
          The ability to draw on image layers is just for fun.
        </p>
      </div>
      <div style="
        padding: var(--spacing-lg);
        display: flex;
        gap: var(--spacing-md);
        justify-content: flex-end;
        background: rgba(0, 0, 0, 0.1);
        border-radius: 0 0 9px 9px;
      ">
        <button id="cancelBtn" style="
          padding: var(--spacing-md) var(--spacing-lg);
          border: 2px solid #6c757d;
          border-radius: var(--border-radius-sm);
          font-family: var(--font-primary);
          font-weight: 600;
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
          background-color: #6c757d;
          color: white;
        ">Cancel</button>
        <button id="unlockBtn" style="
          padding: var(--spacing-md) var(--spacing-lg);
          border: 2px solid var(--accent-color);
          border-radius: var(--border-radius-sm);
          font-family: var(--font-primary);
          font-weight: 600;
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
          background-color: var(--accent-color);
          color: var(--background-color);
        ">Unlock for Session</button>
      </div>
    `;
    
    dialogContainer.appendChild(dialog);
    document.body.appendChild(dialogContainer);
    
    // Handle button clicks with proper cleanup tracking
    let isCleanedUp = false;
    const cancelBtn = dialog.querySelector('#cancelBtn');
    const unlockBtn = dialog.querySelector('#unlockBtn');
    
    const cleanup = () => {
      if (isCleanedUp) return; // Prevent double cleanup
      isCleanedUp = true;
      
      if (document.body.contains(dialogContainer)) {
      document.body.removeChild(dialogContainer);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
    
    cancelBtn.onclick = () => {
      cleanup();
    };
    
    unlockBtn.onclick = () => {
      setIsDrawingUnlocked(true);
      setActiveTool(tool);
      cleanup();
    };
    
    // Close on background click
    dialogContainer.onclick = (e) => {
      if (e.target === dialogContainer) {
        cleanup();
      }
    };
    
    // Close on Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cleanup();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    
  }, []);

  return (
      <div className="page-container-v1 map-editor-page-override">
        <div className="header-strip-v1">
          <div className="header-group logo-title">
            <div className="logo-images">
              <img src="/logo-dnd-crossed-swords.png" alt="D&D Logo" className="header-logo-image" />
            </div>
            <h1 className="header-logo">D&D MAP EXPLORER</h1>
            {isSharedGame && (
              <div className="shared-game-banner-header">
                <span className="shared-badge-header">Shared</span>
                <button className="save-copy-btn-header" onClick={handleSaveAsCopy}>Save Copy</button>
              </div>
            )}
          </div>

          {/* Drawing tools available in both edit and play modes for DM flexibility */}
            <div className="header-group drawing-tools-header">
              <DrawingTools 
                activeTool={activeTool} 
                onToolChange={handleToolChange}
                toolOptions={toolOptions}
                onOptionChange={handleOptionChange}
                isNonWallLayerActive={isNonWallLayerActive}
                onUnlockRequest={handleUnlockRequest}
                isDrawingUnlocked={isDrawingUnlocked}
              />
            </div>

          <div className="header-group game-controls-header">
            <button 
              className={`mode-toggle-btn-header ${isEditMode ? 'edit-mode' : 'play-mode'}`}
              onClick={handleModeToggle}
              title={isEditMode ? 'Switch to Play Mode' : 'Switch to Edit Mode'}
            >
              <i className="fas fa-dice-d20"></i>
            </button>
            
            {/* כפתור יצירת משחק חדש */}
            <button 
              className="new-game-btn-header" 
              onClick={handleCreateNewGame}
              title="Create New Game"
            >
              <i className="fas fa-plus"></i>
            </button>
            
            {/* כפתור שמירה מהירה - רק אם יש לאן לשמור */}
            {mapId && mapId !== 'new' && !isNaN(parseInt(mapId)) && (
              <div className="quick-save-container">
                <button 
                  className="quick-save-btn-header" 
                  onClick={handleQuickSave}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setShowAutoSaveMenu(!showAutoSaveMenu);
                  }}
                  title="Quick Save (Right-click for auto-save options)"
                  disabled={isQuickSaving}
                >
                  {isQuickSaving ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-save"></i>
                  )}
                </button>
                
                {/* Auto-save progress bar */}
                {autoSaveEnabled && (
                  <div className="autosave-progress-bar">
                    <div 
                      className="autosave-progress-fill"
                      style={{ 
                        width: `${((autoSaveInterval - autoSaveTimeLeft) / autoSaveInterval) * 100}%` 
                      }}
                    />
                  </div>
                )}
                
                {/* Auto-save menu */}
                {showAutoSaveMenu && (
                  <div className="autosave-menu">
                    <div className="autosave-menu-content">
                      <div className="autosave-info">
                        <div className="info-row">
                          <span className="info-label">Status:</span>
                          <span className="info-value">{autoSaveEnabled ? 'Enabled' : 'Disabled'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Last saved:</span>
                          <span className="info-value">{lastAutoSave ? new Date(lastAutoSave).toLocaleTimeString() : 'Never'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Next save in:</span>
                          <span className="info-value">{autoSaveTimeLeft}s</span>
                        </div>
                        <div className="info-row interval-row">
                          <span className="info-label">Interval:</span>
                          <input 
                            type="number"
                            min="5"
                            max="300"
                            value={autoSaveInterval}
                            onChange={(e) => {
                              const newInterval = Math.max(5, parseInt(e.target.value) || 5);
                              setAutoSaveInterval(newInterval);
                            }}
                            className="interval-input"
                            title="Auto-save interval (5-300 seconds)"
                          />
                          <span className="info-value">sec</span>
                        </div>
                      </div>
                      
                      <div className="autosave-controls">
                        <button 
                          className="autosave-toggle-btn"
                          onClick={() => {
                            setAutoSaveEnabled(!autoSaveEnabled);
                            setShowAutoSaveMenu(false);
                          }}
                        >
                          {autoSaveEnabled ? (
                            <>
                              <i className="fas fa-pause"></i>
                              Pause Auto-save
                            </>
                          ) : (
                            <>
                              <i className="fas fa-play"></i>
                              Resume Auto-save
                            </>
                          )}
                        </button>
                        
                        {hasUnsavedWork && (
                          <button 
                            className="autosave-restore-btn"
                            onClick={() => {
                              handleRestoreAutoSave();
                              setShowAutoSaveMenu(false);
                            }}
                          >
                            <i className="fas fa-history"></i>
                            Restore Auto-save
                          </button>
                        )}
                      </div>
                      
                      <div className="autosave-tip">
                        <i className="fas fa-lightbulb tip-icon"></i>
                        <span className="tip-text">
                          Your work is automatically saved every {autoSaveInterval} seconds to prevent data loss.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* כפתור שמירה בשם - תמיד זמין */}
            <button 
              className="save-as-btn-header" 
              onClick={handleOpenSaveModal}
              title="Save As..."
            >
              <i className="fas fa-copy"></i>
            </button>
            
            <button 
              className="load-game-btn-header" 
              onClick={handleOpenLoadModal}
              title="Load Game"
            >
              <i className="fas fa-folder-open"></i>
            </button>
            
            <button 
              className="export-local-btn-header" 
              onClick={handleExportLocal}
              title="Export"
            >
              <i className="fas fa-download"></i>
            </button>
            
            <label className="import-local-btn-header" title="Import">
              <i className="fas fa-upload"></i>
              <input 
                type="file" 
                accept=".json" 
                onChange={handleImportLocal} 
                style={{ display: 'none' }}
              />
            </label>
            
            {currentUser ? (
                <Link to="/profile" className="header-button" title="Profile">
                  <i className="fas fa-user"></i>
                </Link>
            ) : (
                <Link to="/login" className="header-button" title="Login">
                  <i className="fas fa-sign-in-alt"></i>
                </Link>
            )}
          </div>
        </div>

        <div className="main-content-v1">
          <div className="canvas-column-v1">
            {mapId ? (
                <MapDisplay 
                  ref={mapDisplayRef}
                  activeTool={activeTool}
                  toolOptions={toolOptions}
                  tokens={tokens}
                  onTokenSelect={handleTokenSelect}
                  onNewTokenCreated={handleNewTokenCreated}
                  selectedTokenIds={selectedTokenIds}
                  mapId={mapId}
                  isEditMode={isEditMode}
                  onUpdateToken={handleUpdateToken}
                  areDrawingsVisible={areDrawingsVisible}
                  onOptionChange={handleOptionChange}
                  onToolChange={handleToolChange}
                  groupLightingEnabled={groupLightingEnabled}
                />
            ) : (
                <div className="loading-placeholder">Loading Map Data...</div>
            )}
          </div>

          <div className={`side-panel-column-v1 ${!isEditMode ? 'play-mode' : ''}`}>
            {isEditMode && mapId && (
              <>
                <UndoRedoButtons 
                  tokens={tokens}
                  onUpdateToken={handleUpdateToken}
                  onDeleteToken={handleDeleteToken}
                  onRestoreToken={handleRestoreToken}
                  onTokenSelect={handleTokenSelect}
                  tokenGroups={tokenGroups}
                  onCreateTokenGroup={handleCreateTokenGroup}
                  onRemoveTokenGroup={handleUngroupTokens}
                  onCreateTokenGroupDirect={createTokenGroupDirect}
                  onRemoveTokenGroupDirect={removeTokenGroupDirect}
                  onUpdateTokenGroupDirect={updateTokenGroupDirect}
                />
                <LayerManager 
                    mapId={mapId} 
                    areDrawingsVisible={areDrawingsVisible}
                    onToggleDrawingsVisibility={toggleDrawingsVisibility}
                    onToolChange={handleToolChange}
                />
                <TokenManager 
                    tokens={tokens}
                    selectedTokenIds={selectedTokenIds}
                    onUpdateToken={handleUpdateToken}
                    onDeleteToken={handleDeleteToken}
                    toolOptions={toolOptions}
                    onOptionChange={handleOptionChange}
                    onToolSelectRequest={handleToolChange}
                    isEditMode={isEditMode}
                    activeTool={activeTool}
                    onTokenSelect={handleTokenSelect}
                    onRestoreToken={handleRestoreToken}
                    onToggleTokenGroup={handleToggleTokenGroup}
                    onChangeGroupColor={handleChangeGroupColor}
                    tokenGroups={tokenGroups}
                    fovAdapter={fovAdapter}
                    groupLightingEnabled={groupLightingEnabled}
                    onToggleGroupLighting={handleToggleGroupLighting}
                  />
              </>
            )}
            {!isEditMode && (
              <>
                {/* Show LayerManager and TokenManager in play mode */}
                {mapId && (
                  <>
                    <LayerManager 
                        mapId={mapId} 
                        areDrawingsVisible={areDrawingsVisible}
                        onToggleDrawingsVisibility={toggleDrawingsVisibility}
                        onToolChange={handleToolChange}
                    />
                    <TokenManager 
                        tokens={tokens}
                        selectedTokenIds={selectedTokenIds}
                        onUpdateToken={handleUpdateToken}
                        onDeleteToken={handleDeleteToken}
                        toolOptions={toolOptions}
                        onOptionChange={handleOptionChange}
                        onToolSelectRequest={handleToolChange}
                        isEditMode={false}
                        activeTool={activeTool}
                        onTokenSelect={handleTokenSelect}
                        onRestoreToken={handleRestoreToken}
                        onToggleTokenGroup={handleToggleTokenGroup}
                        onChangeGroupColor={handleChangeGroupColor}
                        tokenGroups={tokenGroups}
                        fovAdapter={fovAdapter}
                        groupLightingEnabled={groupLightingEnabled}
                        onToggleGroupLighting={handleToggleGroupLighting}
                      />
                  </>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Memory warning for all users */}
        <MemoryWarning />
        
        {/* Debug panel - Admin only, controlled via profile toggle */}
        <HistoryDebugPanel isVisible={!!currentUser?.isAdmin && !!showHistoryDebugPanel} />

        {/* Save/Load Status Message */}
        {saveLoadStatus && (
          <div 
            className="save-load-status-message"
            onMouseEnter={() => setSaveLoadStatus('')}
            style={{ cursor: 'pointer' }}
          >
            {saveLoadStatus}
          </div>
        )}

        {/* Simple notification message */}
        {notification && (
          <div 
            className="save-load-status-message"
            onMouseEnter={() => setNotification('')}
            style={{ cursor: 'pointer' }}
          >
            {notification}
          </div>
        )}

        {/* New Game Confirmation Dialog */}
        {showNewGameConfirm && (
          <div className="confirmation-overlay" onClick={handleCancelNewGame}>
            <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="confirmation-header">
                <h3>Create New Game</h3>
              </div>
              <div className="confirmation-content">
                <p>Are you sure you want to create a new game?</p>
                <p><strong>This action will delete all current content on the map.</strong></p>
              </div>
              <div className="confirmation-buttons">
                <button 
                  className="confirmation-btn cancel-btn" 
                  onClick={handleCancelNewGame}
                >
                  Cancel
                </button>
                <button 
                  className="confirmation-btn confirm-btn" 
                  onClick={handleConfirmNewGame}
                >
                  Create New Game
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Game Modal */}
        <SaveGameModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          onSaveSuccess={handleSaveSuccess}
          gameName={gameName}
          existingGameId={mapId && mapId !== 'new' && !isNaN(parseInt(mapId)) ? mapId : null}
          tokens={tokens}
          selectedTokenIds={selectedTokenIds}
          tokenGroups={tokenGroups}
        />

        {/* Load Game Modal */}
        <LoadGameModal
          isOpen={isLoadModalOpen}
          onClose={() => setIsLoadModalOpen(false)}
          onLoadSuccess={handleLoadSuccess}
          tokens={tokens}
          setTokens={setTokens}
          selectedTokenIds={selectedTokenIds}
          setSelectedTokenIds={setSelectedTokenIds}
          tokenGroups={tokenGroups}
          setTokenGroups={setTokenGroups}
        />
        
        {/* Group Color Picker */}
        <ColorPicker
          isOpen={showGroupColorPicker}
          onClose={handleGroupColorClose}
          initialColor={selectedGroupForColor?.color || '#007bff'}
          onChange={handleGroupColorChange}
          anchorElement={groupColorAnchor}
        />

        {/* Re-Authentication Modal */}
        <ReAuthModal
          isOpen={showReAuthModal}
          onClose={() => setShowReAuthModal(false)}
          onSuccess={() => setShowReAuthModal(false)}
        />

        {/* Unsaved Changes Warning Modal */}
        {showUnsavedChangesModal && (
          <div className="confirmation-overlay" onClick={handleCancelNavigation}>
            <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="confirmation-header">
                <h3>Unsaved Changes</h3>
                <button className="close-btn" onClick={handleCancelNavigation}>×</button>
              </div>
              <div className="confirmation-body">
                <p>You have unsaved changes that will be lost if you continue.</p>
                <p className="warning-text">
                  <i className="fas fa-exclamation-triangle"></i>
                  This action cannot be undone.
                </p>
              </div>
              <div className="confirmation-actions">
                <button className="confirm-btn cancel-btn" onClick={handleCancelNavigation}>
                  Stay Here
                </button>
                <button className="confirm-btn delete-btn" onClick={handleConfirmNavigation}>
                  Leave Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

// Component that handles map loading logic
const MapEditorContent = () => {
  const [mapId, setMapId] = useState(null);
  const [mapCreated, setMapCreated] = useState(false);
  const [gameName, setGameName] = useState('');
  const [isSharedGame, setIsSharedGame] = useState(false);
  const [tokens, setTokensState] = useState([]);
  const [selectedTokenIds, setSelectedTokenIdsState] = useState([]);
  const [tokenGroups, setTokenGroups] = useState([]); // Array of {id, name, tokenIds: []}
  const [nextGroupId, setNextGroupId] = useState(1);
  const [groupLightingEnabled, setGroupLightingEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  const { loadGameState } = useGames();
  
  const layerContext = useLayers();
  const containerContext = useContainers();
  const historyContext = useHistory();

  // Create GameStateManager instance
  const gameStateManager = useMemo(() => new GameStateManager(), []);

  // Ref to track if initial load has been attempted
  const initialLoadAttempted = useRef(false);
  const currentGameIdRef = useRef(null);
  
  // Store stable references to contexts
  const contextRefs = useRef({});
  contextRefs.current = {
    layerContext,
    containerContext,
    historyContext,
    loadGameState
  };
  
  // Wrapper functions with logging
  const setTokens = useCallback((newTokens) => {
    setTokensState(newTokens);
  }, []);
  
  const setSelectedTokenIds = useCallback((newSelectedTokenIds) => {
    setSelectedTokenIdsState(newSelectedTokenIds);
  }, []);
  
  const createNewMap = useCallback(async () => {
    if (mapCreated) return;
    setMapCreated(true);
    setMapId('new'); // Use 'new' for new maps to distinguish from loaded games
    
    // Create initial layers for new game
    setTimeout(() => {
      contextRefs.current.layerContext?.createInitialGameLayers();
    }, 100);
  }, [mapCreated]);
  
  const loadGameData = useCallback(async (gameId, sharedGameId) => {
    try {
      let gameData;
      if (gameId) {
        const loadResult = await gameStateManager.loadGameState(gameId);
        
        if (loadResult.success) {
          const contexts = {
            layerContext: contextRefs.current.layerContext,
            containerContext: contextRefs.current.containerContext,
            gameContext: {},
            historyContext: contextRefs.current.historyContext,
            tokenContext: {
              tokens: [], // Start with empty array for loading
              setTokens: setTokens,
              selectedTokenIds: [], // Start with no selection for loading
              setSelectedTokenIds: setSelectedTokenIds,
              tokenGroups: [],
              setTokenGroups: setTokenGroups
            }
          };
          
          await gameStateManager.restoreGameState(loadResult.gameState, contexts);
          
          gameData = {
            name: loadResult.metadata.name,
            game_name: loadResult.metadata.name
          };
        } else {
          throw new Error(loadResult.error);
        }
      } else if (sharedGameId) {
        // Assuming loadGameState from useGames() is stable or can be memoized/referenced differently if it causes re-renders
        // For now, we directly call it. If issues persist, this might need a rethink.
        const gameContextLoad = contextRefs.current.loadGameState; // Capture the function if it's stable
        gameData = await gameContextLoad(sharedGameId, true); 
      }
      
      if (gameData) {
        setGameName(gameData.game_name || gameData.name);
        setMapId(gameId || sharedGameId);
        
        // שמירת gameId ב-localStorage כדי לזכור את המשחק האחרון
        if (gameId && gameId !== 'new') {
          localStorage.setItem('dnd_last_game_id', gameId);
        }
      }
    } catch (err) { 
      console.error('Error loading game:', err);
      // Since showNotification is not available here, we'll use a simple console error
      // The user will see the error in the console and can try again
      initialLoadAttempted.current = false; // Reset if load fails, to allow retry
    }
      }, [gameStateManager, setTokens, setSelectedTokenIds]);
  
  useEffect(() => {
    const gameId = searchParams.get('gameId');
    const sharedGameId = searchParams.get('sharedGameId');
    const currentGameId = gameId || sharedGameId;
    
    // Reset initialLoadAttempted if gameId has changed
    if (currentGameId !== currentGameIdRef.current) {
      initialLoadAttempted.current = false;
      currentGameIdRef.current = currentGameId;
      
      // Clear previous game state
      setTokensState([]);
      setSelectedTokenIdsState([]);
      setTokenGroups([]);
      setMapId(null);
      setGameName('');
      setMapCreated(false); // Reset mapCreated flag when changing games
    }
    
    // Only run on initial mount or if gameId/sharedGameId changes
    // and initial load has not been attempted for the current IDs
    if (currentGameId && !initialLoadAttempted.current) {
      setIsSharedGame(!!sharedGameId);
      initialLoadAttempted.current = true; // Mark that load is being attempted

      loadGameData(gameId, sharedGameId);
    } else if (!currentGameId && !mapId && !mapCreated && !initialLoadAttempted.current) {
      // If no gameId and no map created yet, create a new one
      createNewMap();
      initialLoadAttempted.current = true; // Mark as attempted for new map scenario too
    }
  }, [searchParams, loadGameData, createNewMap, mapId, mapCreated]);

  // Cleanup effect when component unmounts or gameId changes
  useEffect(() => {
    return () => {
      // Clear contexts on unmount only - don't clear on every render
      if (contextRefs.current.historyContext?.clearHistory) {
        contextRefs.current.historyContext.clearHistory();
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return (
    <MapEditorInner 
      mapId={mapId}
      gameName={gameName}
      setGameName={setGameName}
      isSharedGame={isSharedGame}
      setIsSharedGame={setIsSharedGame}
      tokens={tokens}
      setTokens={setTokens}
      selectedTokenIds={selectedTokenIds}
      setSelectedTokenIds={setSelectedTokenIds}
      tokenGroups={tokenGroups}
                setTokenGroups={setTokenGroups}
          nextGroupId={nextGroupId}
          setNextGroupId={setNextGroupId}
          groupLightingEnabled={groupLightingEnabled}
          setGroupLightingEnabled={setGroupLightingEnabled}
        />
  );
};
  
// Main component - contexts are now provided at App level
const MapEditorPage = () => {
  return <MapEditorContent />;
};
  
export default MapEditorPage; 